import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_CAM = "niveltest.proctor.camId";
const ANALYZER_SAMPLE_MS = 450;
const ANALYZER_WIDTH = 160;
const ANALYZER_HEIGHT = 120;
const VIDEO_READY_TIMEOUT_MS = 5000;
const VERIFY_DURATION_MS = 7200;
const DARK_LUMA_MAX = 34;
const STABLE_LUMA_MIN = 45;
const CONTRAST_MIN = 12;
const CENTER_LUMA_MIN = 42;
const CENTER_CONTRAST_MIN = 8;
const MOTION_DIFF_MIN = 0.65;

const cleanCameraHealth = () => ({
  ready: false,
  videoReady: false,
  ok: true,
  validated: false,
  validating: false,
  dark: false,
  overBright: false,
  lowContrast: false,
  noMotion: false,
  faceChecked: false,
  faceVisible: null,
  faceSource: null,
  reason: "",
  avgLuma: null,
  contrast: null,
  centerLuma: null,
  centerContrast: null,
  motionScore: null,
  motionHits: 0,
  checkedAt: 0,
});

const isSecureOk = () => {
  try {
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    const isLocalhost = /(^|\.)localhost$|^127\.0\.0\.1$|^::1$/.test(host);
    const https =
      typeof window !== "undefined" &&
      (window.isSecureContext || window.location.protocol === "https:");
    return https || isLocalhost;
  } catch {
    return false;
  }
};

export default function useNivelTestProctor({ enabled = true } = {}) {
  const initialCamId =
    typeof window !== "undefined" ? localStorage.getItem(STORAGE_CAM) || "" : "";

  const [proctor, setProctor] = useState({
    ready: false,
    cam: false,
    blocked: false,
    reason: "",
  });

  const [deviceOpts, setDeviceOpts] = useState({
    cams: [],
    camId: initialCamId,
  });

  const [cameraHealth, setCameraHealth] = useState(cleanCameraHealth);

  const selectedCamRef = useRef(initialCamId);
  const streamRef = useRef(null);
  const videoTrackRef = useRef(null);
  const videoElementRef = useRef(null);
  const cameraHealthRef = useRef(cleanCameraHealth());

  const deviceListenerKindRef = useRef(null);
  const deviceChangeHandlerRef = useRef(null);
  const proctorPollRef = useRef(null);
  const startingRef = useRef(false);
  const analyzerIntervalRef = useRef(null);
  const analyzerCanvasRef = useRef(null);
  const previousFrameRef = useRef(null);
  const lastMotionAtRef = useRef(0);

  const setHealth = useCallback((next) => {
    cameraHealthRef.current = next;
    setCameraHealth(next);
  }, []);

  const resetHealth = useCallback(() => {
    previousFrameRef.current = null;
    lastMotionAtRef.current = 0;
    setHealth(cleanCameraHealth());
  }, [setHealth]);

  const getActivePreviewVideo = useCallback(() => videoElementRef.current || null, []);

  const isVideoFrameReady = useCallback((video) => {
    return !!(
      video &&
      video.readyState >= 2 &&
      video.videoWidth > 0 &&
      video.videoHeight > 0
    );
  }, []);

  const stopCameraAnalyzer = useCallback(() => {
    try {
      if (analyzerIntervalRef.current) clearInterval(analyzerIntervalRef.current);
    } catch { }
    analyzerIntervalRef.current = null;
  }, []);

  const refreshDeviceList = useCallback(async () => {
    try {
      const devs = await navigator.mediaDevices.enumerateDevices();
      const cams = devs.filter((d) => d.kind === "videoinput");

      let camId = selectedCamRef.current || "";
      if (camId && !cams.some((d) => d.deviceId === camId)) {
        camId = "";
        selectedCamRef.current = "";
        try {
          localStorage.setItem(STORAGE_CAM, "");
        } catch { }
      }

      setDeviceOpts({ cams, camId });
      return { cams, camId };
    } catch {
      return { cams: [], camId: selectedCamRef.current || "" };
    }
  }, []);

  const updateFromTrack = useCallback((vTrack, why = "") => {
    const camLive = !!vTrack && vTrack.readyState === "live" && vTrack.enabled !== false;
    const blocked = !camLive;
    const reason = blocked ? (why || "Camara no disponible/activa") : "";
    setProctor((p) => ({ ...p, ready: true, cam: camLive, blocked, reason }));
  }, []);

  const detachDeviceListener = useCallback(() => {
    try {
      if (!navigator.mediaDevices) return;
      if (deviceListenerKindRef.current === "addEventListener") {
        navigator.mediaDevices.removeEventListener?.(
          "devicechange",
          deviceChangeHandlerRef.current,
        );
      } else if (deviceListenerKindRef.current === "ondevicechange") {
        if (navigator.mediaDevices.ondevicechange === deviceChangeHandlerRef.current) {
          navigator.mediaDevices.ondevicechange = null;
        }
      }
    } catch { }
    deviceListenerKindRef.current = null;
    deviceChangeHandlerRef.current = null;
  }, []);

  const clearCurrentStream = useCallback(() => {
    const stopTrack = (track) => {
      try {
        if (track && track.readyState !== "ended") track.stop();
      } catch { }
    };

    stopCameraAnalyzer();

    try {
      const currentStream = streamRef.current;
      if (currentStream?.getTracks) currentStream.getTracks().forEach(stopTrack);
    } catch { }

    stopTrack(videoTrackRef.current);

    streamRef.current = null;
    videoTrackRef.current = null;

    if (videoElementRef.current) {
      try {
        videoElementRef.current.pause?.();
      } catch { }
      try {
        videoElementRef.current.srcObject = null;
      } catch { }
      try {
        videoElementRef.current.load?.();
      } catch { }
    }

    resetHealth();
  }, [resetHealth, stopCameraAnalyzer]);

  const waitForCameraFrame = useCallback((timeoutMs = VIDEO_READY_TIMEOUT_MS) => {
    return new Promise((resolve) => {
      const startedAt = Date.now();
      let rafId = null;

      const check = () => {
        const video = getActivePreviewVideo();

        if (isVideoFrameReady(video)) {
          if (rafId) cancelAnimationFrame(rafId);
          resolve(true);
          return;
        }

        if (Date.now() - startedAt >= timeoutMs) {
          if (rafId) cancelAnimationFrame(rafId);
          resolve(false);
          return;
        }

        rafId = requestAnimationFrame(check);
      };

      check();
    });
  }, [getActivePreviewVideo, isVideoFrameReady]);

  const analyzeCameraFrameOnce = useCallback(() => {
    try {
      const video = getActivePreviewVideo();
      const track = videoTrackRef.current;
      const now = Date.now();

      if (!track || track.readyState !== "live" || track.enabled === false) {
        const next = {
          ...cleanCameraHealth(),
          ok: false,
          reason: "No hay una camara activa.",
          checkedAt: now,
        };
        setHealth(next);
        return next;
      }

      if (!isVideoFrameReady(video)) {
        const prev = cameraHealthRef.current || cleanCameraHealth();
        const next = {
          ...prev,
          ready: true,
          videoReady: false,
          ok: false,
          reason: "Esperando imagen de la camara.",
          checkedAt: now,
        };
        setHealth(next);
        return next;
      }

      if (!analyzerCanvasRef.current) {
        analyzerCanvasRef.current = document.createElement("canvas");
      }

      const canvas = analyzerCanvasRef.current;
      canvas.width = ANALYZER_WIDTH;
      canvas.height = ANALYZER_HEIGHT;

      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) {
        const next = {
          ...cleanCameraHealth(),
          ready: true,
          videoReady: true,
          ok: false,
          reason: "No se pudo analizar la imagen de la camara.",
          checkedAt: now,
        };
        setHealth(next);
        return next;
      }

      ctx.drawImage(video, 0, 0, ANALYZER_WIDTH, ANALYZER_HEIGHT);
      const imageData = ctx.getImageData(0, 0, ANALYZER_WIDTH, ANALYZER_HEIGHT);
      const pixels = imageData.data;
      const gray = new Float32Array(ANALYZER_WIDTH * ANALYZER_HEIGHT);

      let sum = 0;
      for (let i = 0, p = 0; i < pixels.length; i += 4, p += 1) {
        const luma = (pixels[i] * 0.299) + (pixels[i + 1] * 0.587) + (pixels[i + 2] * 0.114);
        gray[p] = luma;
        sum += luma;
      }

      const totalPixels = gray.length || 1;
      const avgLuma = sum / totalPixels;

      let variance = 0;
      for (let i = 0; i < gray.length; i += 1) {
        const diff = gray[i] - avgLuma;
        variance += diff * diff;
      }
      const contrast = Math.sqrt(variance / totalPixels);

      const centerX0 = Math.floor(ANALYZER_WIDTH * 0.28);
      const centerX1 = Math.ceil(ANALYZER_WIDTH * 0.72);
      const centerY0 = Math.floor(ANALYZER_HEIGHT * 0.18);
      const centerY1 = Math.ceil(ANALYZER_HEIGHT * 0.82);

      let centerSum = 0;
      let centerVarianceSeed = 0;
      let centerCount = 0;

      for (let y = centerY0; y < centerY1; y += 1) {
        for (let x = centerX0; x < centerX1; x += 1) {
          const idx = (y * ANALYZER_WIDTH) + x;
          centerSum += gray[idx];
          centerVarianceSeed += gray[idx] * gray[idx];
          centerCount += 1;
        }
      }

      const centerLuma = centerCount > 0 ? centerSum / centerCount : avgLuma;
      const centerVariance = centerCount > 0
        ? Math.max((centerVarianceSeed / centerCount) - (centerLuma * centerLuma), 0)
        : variance / totalPixels;
      const centerContrast = Math.sqrt(centerVariance);

      let motionScore = 0;
      if (previousFrameRef.current && previousFrameRef.current.length === gray.length) {
        let diffSum = 0;
        for (let i = 0; i < gray.length; i += 1) {
          diffSum += Math.abs(gray[i] - previousFrameRef.current[i]);
        }
        motionScore = diffSum / totalPixels;
      }
      previousFrameRef.current = gray;

      const motionDetected = motionScore >= MOTION_DIFF_MIN;
      if (motionDetected) {
        lastMotionAtRef.current = now;
      }

      const prev = cameraHealthRef.current || cleanCameraHealth();
      const motionHits = motionDetected ? Number(prev.motionHits || 0) + 1 : Number(prev.motionHits || 0);
      const noMotion = motionHits === 0 && (now - (lastMotionAtRef.current || now)) >= 2000;

      const dark = avgLuma < DARK_LUMA_MAX || centerLuma < CENTER_LUMA_MIN;
      const lowContrast = contrast < CONTRAST_MIN || centerContrast < CENTER_CONTRAST_MIN;
      const faceVisible = !dark && centerLuma >= CENTER_LUMA_MIN && centerContrast >= CENTER_CONTRAST_MIN;

      let reason = "";
      if (dark) reason = "Necesitamos mas luz para verte bien.";
      else if (lowContrast) reason = "La imagen se ve plana o borrosa.";
      else if (!faceVisible) reason = "Coloca tu rostro dentro del encuadre.";
      else if (noMotion) reason = "Muevete ligeramente para validar presencia.";

      const next = {
        ...prev,
        ready: true,
        videoReady: true,
        ok: !reason,
        dark,
        overBright: false,
        lowContrast,
        noMotion,
        faceChecked: true,
        faceVisible,
        faceSource: faceVisible ? "quality_fallback" : null,
        reason,
        avgLuma: Math.round(avgLuma),
        contrast: Number(contrast.toFixed(1)),
        centerLuma: Math.round(centerLuma),
        centerContrast: Number(centerContrast.toFixed(1)),
        motionScore: Number(motionScore.toFixed(2)),
        motionHits,
        checkedAt: now,
      };

      setHealth(next);
      return next;
    } catch {
      const next = {
        ...cleanCameraHealth(),
        ready: true,
        ok: false,
        reason: "No se pudo validar la camara.",
        checkedAt: Date.now(),
      };
      setHealth(next);
      return next;
    }
  }, [getActivePreviewVideo, isVideoFrameReady, setHealth]);

  const startCameraAnalyzer = useCallback(() => {
    stopCameraAnalyzer();
    resetHealth();
    analyzeCameraFrameOnce();
    analyzerIntervalRef.current = setInterval(() => {
      analyzeCameraFrameOnce();
    }, ANALYZER_SAMPLE_MS);
  }, [analyzeCameraFrameOnce, resetHealth, stopCameraAnalyzer]);

  const attachStreamToVideo = useCallback((video, stream = streamRef.current) => {
    if (!video) return;
    try {
      video.srcObject = stream || null;
      video.muted = true;
      video.playsInline = true;
      video.autoplay = true;
      if (stream) {
        video.onloadeddata = () => {
          updateFromTrack(videoTrackRef.current);
          startCameraAnalyzer();
        };
        video.play?.().catch(() => {});
      } else {
        video.onloadeddata = null;
        video.pause?.();
        video.load?.();
      }
    } catch {}
  }, [startCameraAnalyzer, updateFromTrack]);

  const bindVideoRef = useCallback((node) => {
    videoElementRef.current = node || null;
    if (!node) return;
    attachStreamToVideo(node);
  }, [attachStreamToVideo]);

  const assertSelectedDeviceWasUsed = useCallback((stream, preferredDeviceId) => {
    if (!preferredDeviceId) return true;
    const actualDeviceId = stream?.getVideoTracks?.()[0]?.getSettings?.().deviceId || "";
    return !actualDeviceId || actualDeviceId === preferredDeviceId;
  }, []);

  const stop = useCallback(() => {
    detachDeviceListener();

    try {
      if (proctorPollRef.current) clearInterval(proctorPollRef.current);
    } catch { }
    proctorPollRef.current = null;

    clearCurrentStream();

    setProctor({ ready: false, cam: false, blocked: false, reason: "" });
  }, [detachDeviceListener, clearCurrentStream]);

  const openVideo = useCallback(async (preferredDeviceId) => {
    const base = {
      width: { ideal: 640 },
      height: { ideal: 480 },
      aspectRatio: { ideal: 4 / 3 },
      facingMode: "user",
    };

    const tries = [];

    if (preferredDeviceId) {
      tries.push({ video: { ...base, deviceId: { exact: preferredDeviceId } } });
      tries.push({ video: { ...base, deviceId: preferredDeviceId } });
    } else {
      tries.push({ video: base });
      tries.push({ video: { facingMode: "user" } });
      tries.push({ video: true });
    }

    for (const constraints of tries) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (!assertSelectedDeviceWasUsed(stream, preferredDeviceId)) {
          try { stream.getTracks().forEach((track) => track.stop()); } catch { }
          continue;
        }
        return stream;
      } catch { }
    }

    return null;
  }, [assertSelectedDeviceWasUsed]);

  const applyStream = useCallback((stream) => {
    streamRef.current = stream;
    const vTrack = stream.getVideoTracks()[0] || null;
    videoTrackRef.current = vTrack;
    updateFromTrack(vTrack);

    attachStreamToVideo(videoElementRef.current, stream);

    const recheck = () => updateFromTrack(videoTrackRef.current);
    if (vTrack) {
      vTrack.onended = () => updateFromTrack(null, "Camara finalizo");
      vTrack.onmute = recheck;
      vTrack.onunmute = recheck;
    }

    startCameraAnalyzer();
  }, [attachStreamToVideo, startCameraAnalyzer, updateFromTrack]);

  const attachDeviceListener = useCallback(() => {
    const onDeviceChange = async () => {
      try {
        const { cams } = await refreshDeviceList();
        const hasCam = cams.some((d) => d.kind === "videoinput" || d.deviceId);
        if (!hasCam) {
          updateFromTrack(null, "Camara desconectada");
          clearCurrentStream();
          return;
        }

        const selected = selectedCamRef.current || "";
        const track = videoTrackRef.current;
        const settingsDeviceId = track?.getSettings?.().deviceId || "";
        const selectedChanged = !!selected && !!settingsDeviceId && selected !== settingsDeviceId;
        const needRestart = !track || track.readyState !== "live" || selectedChanged;

        if (needRestart) {
          const newStream = await openVideo(selected);
          if (newStream) {
            clearCurrentStream();
            applyStream(newStream);
          } else {
            updateFromTrack(null, "No se pudo reabrir camara");
          }
        } else {
          updateFromTrack(track);
        }
      } catch {
        updateFromTrack(videoTrackRef.current, "Cambio de dispositivo");
      }
    };

    detachDeviceListener();
    deviceChangeHandlerRef.current = onDeviceChange;

    if (navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener("devicechange", onDeviceChange);
      deviceListenerKindRef.current = "addEventListener";
    } else if (navigator.mediaDevices) {
      navigator.mediaDevices.ondevicechange = onDeviceChange;
      deviceListenerKindRef.current = "ondevicechange";
    }
  }, [
    applyStream,
    clearCurrentStream,
    detachDeviceListener,
    openVideo,
    refreshDeviceList,
    updateFromTrack,
  ]);

  const start = useCallback(async (forcedDeviceId = null) => {
    if (!enabled) return false;
    if (startingRef.current) return false;
    startingRef.current = true;

    try {
      if (!navigator?.mediaDevices?.getUserMedia) {
        setProctor({
          ready: true,
          cam: false,
          blocked: true,
          reason: "Tu navegador no soporta camara.",
        });
        return false;
      }

      if (!isSecureOk()) {
        setProctor({
          ready: true,
          cam: false,
          blocked: true,
          reason: "Debes usar HTTPS o localhost para habilitar la camara.",
        });
        return false;
      }

      const requestedId =
        forcedDeviceId ??
        selectedCamRef.current ??
        (typeof window !== "undefined" ? localStorage.getItem(STORAGE_CAM) || "" : "");

      if (forcedDeviceId != null) {
        selectedCamRef.current = requestedId || "";
        try {
          localStorage.setItem(STORAGE_CAM, requestedId || "");
        } catch { }
      }

      await refreshDeviceList();
      clearCurrentStream();

      const stream = await openVideo(requestedId);
      if (!stream) {
        setProctor({
          ready: true,
          cam: false,
          blocked: true,
          reason: "No se pudo iniciar camara (verifica permisos/dispositivos).",
        });
        return false;
      }

      applyStream(stream);
      attachDeviceListener();

      if (proctorPollRef.current) clearInterval(proctorPollRef.current);
      proctorPollRef.current = setInterval(() => {
        updateFromTrack(videoTrackRef.current);
      }, 1500);

      const actualDeviceId =
        stream.getVideoTracks?.()[0]?.getSettings?.().deviceId || requestedId || "";

      selectedCamRef.current = actualDeviceId || requestedId || "";
      setDeviceOpts((prev) => ({
        ...prev,
        camId: actualDeviceId || requestedId || "",
      }));

      return true;
    } finally {
      startingRef.current = false;
    }
  }, [
    applyStream,
    attachDeviceListener,
    clearCurrentStream,
    enabled,
    openVideo,
    refreshDeviceList,
    updateFromTrack,
  ]);

  const retry = useCallback(async (forcedDeviceId = null) => {
    const nextId = forcedDeviceId ?? selectedCamRef.current ?? "";
    stop();
    setProctor({ ready: false, cam: false, blocked: true, reason: "Reintentando..." });
    const ok = await start(nextId);
    if (!ok) setProctor((prev) => ({ ...prev, blocked: true }));
    return ok;
  }, [start, stop]);

  const setSelectedCam = useCallback((id) => {
    const value = id || "";
    selectedCamRef.current = value;

    try {
      localStorage.setItem(STORAGE_CAM, value);
    } catch { }

    setDeviceOpts((prev) => ({ ...prev, camId: value }));
    setHealth({
      ...cleanCameraHealth(),
      validated: false,
      validating: false,
      reason: "Camara cambiada. Validala nuevamente.",
    });
  }, [setHealth]);

  const verifyCameraBeforeExam = useCallback(async ({ durationMs = VERIFY_DURATION_MS } = {}) => {
    const frameReady = await waitForCameraFrame();
    if (!frameReady) {
      setHealth({
        ...cleanCameraHealth(),
        ready: true,
        videoReady: false,
        ok: false,
        validated: false,
        validating: false,
        reason: "No se pudo obtener imagen de la camara.",
        checkedAt: Date.now(),
      });
      return false;
    }

    setHealth({
      ...(cameraHealthRef.current || cleanCameraHealth()),
      ready: true,
      validating: true,
      validated: false,
      reason: "",
    });

    const startedAt = Date.now();
    let last = cameraHealthRef.current || cleanCameraHealth();

    while (Date.now() - startedAt < durationMs) {
      last = analyzeCameraFrameOnce();
      const movementOk =
        Number(last.motionScore || 0) >= MOTION_DIFF_MIN ||
        Number(last.motionHits || 0) > 0;
      const ready =
        last.videoReady === true &&
        last.dark !== true &&
        last.lowContrast !== true &&
        last.faceVisible === true &&
        movementOk;

      if (ready) {
        setHealth({
          ...last,
          ok: true,
          validating: false,
          validated: true,
          noMotion: false,
          reason: "",
        });
        return true;
      }

      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, ANALYZER_SAMPLE_MS));
    }

    last = cameraHealthRef.current || cleanCameraHealth();
    setHealth({
      ...last,
      ok: false,
      validating: false,
      validated: false,
      reason: last.reason || "La camara todavia no cumple las condiciones de validacion.",
    });
    return false;
  }, [analyzeCameraFrameOnce, setHealth, waitForCameraFrame]);

  const captureBlob = useCallback(async () => {
    try {
      const video = videoElementRef.current;
      const track = videoTrackRef.current;
      if (!video || !track || track.readyState !== "live") return null;

      const width = video.videoWidth || 640;
      const height = video.videoHeight || 480;
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      ctx.drawImage(video, 0, 0, width, height);

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.7));
      return blob || null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    return () => stop();
  }, [stop]);

  return {
    proctor,
    deviceOpts,
    videoRef: bindVideoRef,
    start,
    stop,
    retry,
    setSelectedCam,
    captureBlob,
    refreshDeviceList,
    cameraHealth,
    verifyCameraBeforeExam,
  };
}
